import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import {
  Bold,
  Italic,
  Code,
  SquareCode,
  Quote,
  Link as LinkIcon,
  Paperclip,
  Smile,
  Send,
  ChevronDown,
  Type,
  Loader2,
  AtSign,
  Globe,
  Check,
  X,
} from 'lucide-react';
import { EmojiPicker } from './EmojiPicker';
import { MentionAutocomplete } from './MentionAutocomplete';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useChat } from '../../context/ChatContext';
import { useLanguage, TRANSLATION_TARGET_LANGUAGES } from '../../hooks/useLanguage';
import { api } from '../../lib/api';
import type { WorkspaceMember } from '@sahaj/types';

interface MessageComposerProps {
  onSendMessage: (content: string) => Promise<void>;
  placeholder?: string;
}

const CODE_LANGUAGES = [
  { id: '', label: 'Plain Text' },
  { id: 'javascript', label: 'JavaScript' },
  { id: 'typescript', label: 'TypeScript' },
  { id: 'java', label: 'Java' },
  { id: 'python', label: 'Python' },
  { id: 'json', label: 'JSON' },
  { id: 'html', label: 'HTML' },
  { id: 'css', label: 'CSS' },
  { id: 'sql', label: 'SQL' },
  { id: 'bash', label: 'Bash' },
];

const TEXT_SIZES = [
  {
    tag: 'h1',
    label: 'Large (Heading 1)',
    sizeClass: 'text-base font-bold',
  },
  {
    tag: 'h2',
    label: 'Medium (Heading 2)',
    sizeClass: 'text-sm font-bold',
  },
  {
    tag: 'h3',
    label: 'Small (Heading 3)',
    sizeClass: 'text-xs font-semibold',
  },
  {
    tag: 'p',
    label: 'Normal Text',
    sizeClass: 'text-xs',
  },
];

/**
 * Converts rich HTML from contentEditable into clean Markdown
 * for transmission and storage.
 */
export function htmlToMarkdown(element: HTMLElement): string {
  function processNode(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || '';
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return '';
    }

    const el = node as HTMLElement;
    const tagName = el.tagName.toLowerCase();

    if (tagName === 'br') {
      return '\n';
    }

    if (el.hasAttribute('data-mention-id')) {
      const mentionId = el.getAttribute('data-mention-id');
      return `<@${mentionId}> `;
    }

    let childText = '';

    el.childNodes.forEach((child) => {
      childText += processNode(child);
    });

    switch (tagName) {
      case 'b':
      case 'strong':
        return childText.trim()
          ? `**${childText}**`
          : '';

      case 'i':
      case 'em':
        return childText.trim()
          ? `*${childText}*`
          : '';

      case 'strike':
      case 'del':
      case 's':
        return childText.trim()
          ? `~~${childText}~~`
          : '';

      case 'code':
        // If parent is pre, handle in pre.
        if (
          el.parentElement &&
          el.parentElement.tagName.toLowerCase() === 'pre'
        ) {
          return childText;
        }

        return childText.trim()
          ? `\`${childText}\``
          : '';

      case 'pre': {
        const lang =
          el.getAttribute('data-language') || '';

        const trimmedCode =
          childText.replace(/^\n+|\n+$/g, '');

        return `\n\`\`\`${lang}\n${trimmedCode}\n\`\`\`\n`;
      }

      case 'blockquote':
        return `\n> ${childText.replace(/\n/g, '\n> ')}\n`;

      case 'h1':
        return `\n# ${childText.trim()}\n`;

      case 'h2':
        return `\n## ${childText.trim()}\n`;

      case 'h3':
        return `\n### ${childText.trim()}\n`;

      case 'a': {
        const href =
          el.getAttribute('href') || childText;

        return `[${childText.trim() || href}](${href})`;
      }

      case 'div':
      case 'p':
        return childText
          ? `\n${childText}`
          : '\n';

      default:
        return childText;
    }
  }

  return processNode(element)
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export const MessageComposer: React.FC<MessageComposerProps> = ({
  onSendMessage,
  placeholder = 'Write a message...',
}) => {
  const { currentWorkspace, members = [] } = useWorkspace() || {
    currentWorkspace: null,
    members: [],
  };

  const { activeChat } = useChat();

  const { preferredTranslationLanguage, getLanguageName, t } = useLanguage();

  const [isEmpty, setIsEmpty] = useState(true);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showCodeLangMenu, setShowCodeLangMenu] = useState(false);
  const [showTextSizeMenu, setShowTextSizeMenu] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isUploadingAttachment, setIsUploadingAttachment] =
    useState(false);

  // Pre-Send Composer Translation State
  const [composerTargetLang, setComposerTargetLang] = useState<string>(
    preferredTranslationLanguage || 'hi'
  );
  const [showTranslateBar, setShowTranslateBar] = useState(false);
  const [showComposerLangMenu, setShowComposerLangMenu] = useState(false);
  const [isTranslatingDraft, setIsTranslatingDraft] = useState(false);
  const [draftTranslation, setDraftTranslation] = useState<string | null>(null);
  const [draftTranslateError, setDraftTranslateError] = useState<string | null>(null);

  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const codeMenuRef = useRef<HTMLDivElement>(null);
  const textSizeMenuRef = useRef<HTMLDivElement>(null);
  const emojiMenuRef = useRef<HTMLDivElement>(null);
  const translateMenuRef = useRef<HTMLDivElement>(null);

  // Mention autocomplete state
  const [mentionQuery, setMentionQuery] =
    useState<string | null>(null);

  const [, setMentionStartIndex] =
    useState<number>(-1);

  const [selectedMentionIndex, setSelectedMentionIndex] =
    useState<number>(0);

  // Close menus on clicking outside.
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        codeMenuRef.current &&
        !codeMenuRef.current.contains(
          e.target as Node
        )
      ) {
        setShowCodeLangMenu(false);
      }

      if (
        textSizeMenuRef.current &&
        !textSizeMenuRef.current.contains(
          e.target as Node
        )
      ) {
        setShowTextSizeMenu(false);
      }

      if (
        emojiMenuRef.current &&
        !emojiMenuRef.current.contains(
          e.target as Node
        )
      ) {
        setShowEmojiPicker(false);
      }

      if (
        translateMenuRef.current &&
        !translateMenuRef.current.contains(
          e.target as Node
        )
      ) {
        setShowComposerLangMenu(false);
      }
    };

    document.addEventListener(
      'mousedown',
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        'mousedown',
        handleClickOutside
      );
    };
  }, []);

  const checkEmpty = useCallback(() => {
    if (!editorRef.current) {
      setIsEmpty(true);
      return;
    }

    const text =
      editorRef.current.innerText.trim();

    setIsEmpty(text.length === 0);
  }, []);

  /**
   * Get plain text before the current caret.
   *
   * This replaces the textarea-based selection logic from the
   * conflicting branch while keeping the WYSIWYG editor.
   */
  const getTextBeforeCaret = useCallback(() => {
    const editor = editorRef.current;
    const selection = window.getSelection();

    if (
      !editor ||
      !selection ||
      selection.rangeCount === 0
    ) {
      return '';
    }

    const range = selection.getRangeAt(0);

    if (!editor.contains(range.startContainer)) {
      return '';
    }

    const beforeRange = document.createRange();

    beforeRange.selectNodeContents(editor);
    beforeRange.setEnd(
      range.startContainer,
      range.startOffset
    );

    return beforeRange.toString();
  }, []);

  /**
   * Detect an active @mention from the current caret position.
   */
  const detectMention = useCallback(() => {
    const textBeforeCaret =
      getTextBeforeCaret();

    if (!textBeforeCaret) {
      setMentionQuery(null);
      setMentionStartIndex(-1);
      return;
    }

    const lastAtIdx =
      textBeforeCaret.lastIndexOf('@');

    if (lastAtIdx === -1) {
      setMentionQuery(null);
      setMentionStartIndex(-1);
      return;
    }

    const charBeforeAt =
      lastAtIdx > 0
        ? textBeforeCaret[lastAtIdx - 1]
        : ' ';

    const textAfterAt =
      textBeforeCaret.slice(lastAtIdx + 1);

    /**
     * Mention must:
     * - start at beginning or after whitespace
     * - contain no spaces
     */
    const validStart =
      lastAtIdx === 0 ||
      charBeforeAt === ' ' ||
      charBeforeAt === '\n';

    const hasInvalidSpace =
      textAfterAt.includes(' ');

    if (
      validStart &&
      !hasInvalidSpace
    ) {
      setMentionQuery(textAfterAt);
      setMentionStartIndex(lastAtIdx);
      setSelectedMentionIndex(0);
      return;
    }

    setMentionQuery(null);
    setMentionStartIndex(-1);
  }, [getTextBeforeCaret]);

  /**
   * Handle contentEditable changes.
   */
  const handleEditorInput = () => {
    checkEmpty();
    detectMention();
  };

  /**
   * Select a workspace member from autocomplete.
   *
   * The old branch used textareaRef and string slicing.
   * Because the application uses contentEditable, this implementation
   * replaces the current @query directly in the browser selection.
   */
  const handleSelectMention = (
    member: WorkspaceMember
  ) => {
    const editor = editorRef.current;
    const selection = window.getSelection();

    if (
      !editor ||
      !selection ||
      selection.rangeCount === 0
    ) {
      return;
    }

    const range = selection.getRangeAt(0);

    if (!editor.contains(range.startContainer)) {
      return;
    }

    const textBeforeCaret =
      getTextBeforeCaret();

    const lastAtIdx =
      textBeforeCaret.lastIndexOf('@');

    if (lastAtIdx === -1) {
      return;
    }

    const queryLength =
      textBeforeCaret.length - lastAtIdx;

    /**
     * Move the current selection backward over
     * "@query".
     *
     * Selection.modify is supported by the browsers
     * targeted by this web application.
     */
    try {
      selection.collapse(
        range.startContainer,
        range.startOffset
      );

      for (
        let i = 0;
        i < queryLength;
        i += 1
      ) {
        selection.modify(
          'extend',
          'backward',
          'character'
        );
      }

      const mentionNode = document.createElement('span');
      mentionNode.className =
        'mention-pill inline-flex items-center px-1.5 py-0.5 mx-0.5 rounded text-xs font-semibold bg-primary/15 text-primary border border-primary/20 select-all align-baseline';
      mentionNode.setAttribute('data-mention-id', member.user.id);
      mentionNode.setAttribute('contenteditable', 'false');
      mentionNode.textContent = `@${member.user.displayName}`;

      const spaceNode = document.createTextNode('\u00A0');

      if (!selection.isCollapsed) {
        const selRange = selection.getRangeAt(0);
        selRange.deleteContents();
        selRange.insertNode(spaceNode);
        selRange.insertNode(mentionNode);
      } else {
        range.insertNode(spaceNode);
        range.insertNode(mentionNode);
      }

      const newRange = document.createRange();
      newRange.setStartAfter(spaceNode);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
    } catch (error) {
      console.error(
        'Failed to insert mention',
        error
      );

      /**
       * Fallback for browsers where Selection.modify
       * is unavailable.
       */
      editor.focus();

      document.execCommand(
        'insertText',
        false,
        `@${member.user.displayName} `
      );
    }

    setMentionQuery(null);
    setMentionStartIndex(-1);
    setSelectedMentionIndex(0);

    checkEmpty();
    editor.focus();
  };

  const filteredMembers =
    mentionQuery !== null
      ? (members as WorkspaceMember[])
          .filter((member) =>
            member.user.displayName
              .toLowerCase()
              .includes(
                mentionQuery.toLowerCase()
              )
          )
          .slice(0, 8)
      : [];

  /**
   * Handle keyboard shortcuts, mention navigation,
   * and message submission.
   */
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (
      mentionQuery !== null &&
      filteredMembers.length > 0
    ) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();

        setSelectedMentionIndex(
          (prev) =>
            (prev + 1) %
            filteredMembers.length
        );

        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();

        setSelectedMentionIndex(
          (prev) =>
            (prev -
              1 +
              filteredMembers.length) %
            filteredMembers.length
        );

        return;
      }

      if (
        e.key === 'Enter' ||
        e.key === 'Tab'
      ) {
        e.preventDefault();

        const selected =
          filteredMembers[
            selectedMentionIndex
          ] ||
          filteredMembers[0];

        if (selected) {
          handleSelectMention(selected);
          return;
        }
      }

      if (e.key === 'Escape') {
        e.preventDefault();

        setMentionQuery(null);
        setMentionStartIndex(-1);

        return;
      }
    }

    if (
      e.key === 'Enter' &&
      !e.shiftKey
    ) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = async () => {
    if (
      !editorRef.current ||
      isSending
    ) {
      return;
    }

    const markdown =
      htmlToMarkdown(
        editorRef.current
      );

    if (!markdown.trim()) {
      return;
    }

    try {
      setIsSending(true);

      await onSendMessage(
        markdown.trim()
      );

      editorRef.current.innerHTML = '';

      setIsEmpty(true);
      setDraftTranslation(null);
      setShowTranslateBar(false);
      setMentionQuery(null);
      setMentionStartIndex(-1);
      setSelectedMentionIndex(0);
    } catch (err) {
      console.error(
        'Failed to send message',
        err
      );
    } finally {
      setIsSending(false);
    }
  };

  /** Translate draft text in real-time before sending */
  const handleTranslateDraft = async (overrideTargetLang?: string) => {
    if (!editorRef.current) return;
    const rawMarkdown = htmlToMarkdown(editorRef.current).trim();
    if (!rawMarkdown) return;

    const langToUse = overrideTargetLang || composerTargetLang || 'hi';

    try {
      setIsTranslatingDraft(true);
      setDraftTranslateError(null);
      setShowTranslateBar(true);

      const res = await api.translation.translate({
        text: rawMarkdown,
        targetLanguage: langToUse,
      });

      setDraftTranslation(res.translatedText);
    } catch (err: any) {
      console.error('Failed to translate composer draft:', err);
      setDraftTranslateError(t('chat.translationFailed'));
    } finally {
      setIsTranslatingDraft(false);
    }
  };

  /** Replace the content inside the editor with the translated text */
  const handleApplyTranslationToEditor = () => {
    if (!draftTranslation || !editorRef.current) return;
    editorRef.current.innerText = draftTranslation;
    setDraftTranslation(null);
    setShowTranslateBar(false);
    checkEmpty();
    editorRef.current.focus();
  };

  /** Directly translate the draft and send it */
  const handleTranslateAndSend = async () => {
    if (!editorRef.current || isSending) return;

    let textToSend = draftTranslation;

    if (!textToSend) {
      const rawMarkdown = htmlToMarkdown(editorRef.current).trim();
      if (!rawMarkdown) return;

      try {
        setIsSending(true);
        const res = await api.translation.translate({
          text: rawMarkdown,
          targetLanguage: composerTargetLang,
        });
        textToSend = res.translatedText;
      } catch (err) {
        console.error('Failed to translate before send:', err);
        textToSend = rawMarkdown; // fallback to sending original
      } finally {
        setIsSending(false);
      }
    }

    if (textToSend && textToSend.trim()) {
      try {
        setIsSending(true);
        await onSendMessage(textToSend.trim());
        if (editorRef.current) {
          editorRef.current.innerHTML = '';
        }
        setDraftTranslation(null);
        setShowTranslateBar(false);
        setIsEmpty(true);
      } catch (err) {
        console.error('Failed to send translated message:', err);
      } finally {
        setIsSending(false);
      }
    }
  };

  /** Execute native rich formatting command. */
  const executeCommand = (
    command: string,
    value: string | undefined = undefined
  ) => {
    editorRef.current?.focus();

    document.execCommand(
      command,
      false,
      value
    );

    checkEmpty();
  };

  /** Apply Text Size / Heading format. */
  const applyTextSize = (
    tag: string
  ) => {
    setShowTextSizeMenu(false);

    editorRef.current?.focus();

    if (tag === 'p') {
      document.execCommand(
        'formatBlock',
        false,
        '<p>'
      );
    } else {
      document.execCommand(
        'formatBlock',
        false,
        `<${tag}>`
      );
    }

    checkEmpty();
  };

  /** Insert styled inline code. */
  const insertInlineCode = () => {
    editorRef.current?.focus();

    const selection =
      window.getSelection();

    if (
      !selection ||
      selection.rangeCount === 0
    ) {
      return;
    }

    const range =
      selection.getRangeAt(0);

    const selectedText =
      range.toString();

    const codeElement =
      document.createElement('code');

    codeElement.className =
      'px-1.5 py-0.5 mx-0.5 rounded text-xs font-mono bg-muted text-foreground border border-border inline-block align-baseline';

    codeElement.textContent =
      selectedText || 'code';

    range.deleteContents();
    range.insertNode(
      codeElement
    );

    // Place cursor after code.
    const newRange =
      document.createRange();

    newRange.setStartAfter(
      codeElement
    );

    newRange.collapse(true);

    selection.removeAllRanges();
    selection.addRange(newRange);

    checkEmpty();
  };

  /** Insert code block with optional language. */
  const insertCodeBlock = (
    languageId: string = ''
  ) => {
    setShowCodeLangMenu(false);

    editorRef.current?.focus();

    const selection =
      window.getSelection();

    if (
      !selection ||
      selection.rangeCount === 0
    ) {
      return;
    }

    const range =
      selection.getRangeAt(0);

    const selectedText =
      range.toString();

    const preElement =
      document.createElement('pre');

    preElement.setAttribute(
      'data-language',
      languageId
    );

    preElement.className =
      'my-2 p-3.5 rounded-xl border border-slate-800 bg-[#0F172A] text-xs font-mono text-[#E2E8F0] overflow-x-auto whitespace-pre leading-relaxed';

    const codeElement =
      document.createElement('code');

    codeElement.className =
      'font-mono text-[#E2E8F0]';

    codeElement.textContent =
      selectedText ||
      '// write code here';

    preElement.appendChild(
      codeElement
    );

    range.deleteContents();
    range.insertNode(
      preElement
    );

    // Insert a new paragraph after pre so user
    // can continue typing outside the code block.
    const pElement =
      document.createElement('p');

    pElement.innerHTML = '<br>';

    if (preElement.nextSibling) {
      editorRef.current?.insertBefore(
        pElement,
        preElement.nextSibling
      );
    } else {
      editorRef.current?.appendChild(
        pElement
      );
    }

    const newRange =
      document.createRange();

    newRange.setStart(
      codeElement,
      0
    );

    newRange.collapse(true);

    selection.removeAllRanges();
    selection.addRange(newRange);

    checkEmpty();
  };

  /** Insert blockquote. */
  const insertQuote = () => {
    editorRef.current?.focus();

    const selection =
      window.getSelection();

    if (
      !selection ||
      selection.rangeCount === 0
    ) {
      return;
    }

    const range =
      selection.getRangeAt(0);

    const selectedText =
      range.toString();

    const quoteElement =
      document.createElement(
        'blockquote'
      );

    quoteElement.className =
      'border-l-4 border-primary/60 bg-muted/30 px-3.5 py-1.5 my-2 rounded-r text-sm text-foreground/90 italic';

    quoteElement.textContent =
      selectedText || 'Quote text';

    range.deleteContents();
    range.insertNode(
      quoteElement
    );

    const pElement =
      document.createElement('p');

    pElement.innerHTML = '<br>';

    if (quoteElement.nextSibling) {
      editorRef.current?.insertBefore(
        pElement,
        quoteElement.nextSibling
      );
    } else {
      editorRef.current?.appendChild(
        pElement
      );
    }

    const newRange =
      document.createRange();

    newRange.setStartAfter(
      quoteElement
    );

    newRange.collapse(true);

    selection.removeAllRanges();
    selection.addRange(newRange);

    checkEmpty();
  };

  /** Insert link. */
  const insertLink = () => {
    editorRef.current?.focus();

    const selection =
      window.getSelection();

    if (
      !selection ||
      selection.rangeCount === 0
    ) {
      return;
    }

    const range =
      selection.getRangeAt(0);

    const selectedText =
      range.toString();

    const url = prompt(
      'Enter link URL (e.g. https://example.com):',
      selectedText.startsWith('http')
        ? selectedText
        : 'https://'
    );

    if (!url) {
      return;
    }

    const linkElement =
      document.createElement('a');

    linkElement.href = url;
    linkElement.target = '_blank';
    linkElement.rel =
      'noopener noreferrer';

    linkElement.className =
      'text-primary underline font-medium';

    linkElement.textContent =
      selectedText || url;

    range.deleteContents();
    range.insertNode(
      linkElement
    );

    const newRange =
      document.createRange();

    newRange.setStartAfter(
      linkElement
    );

    newRange.collapse(true);

    selection.removeAllRanges();
    selection.addRange(newRange);

    checkEmpty();
  };

  /** Insert emoji at cursor. */
  const insertEmoji = (
    emoji: string
  ) => {
    editorRef.current?.focus();

    const selection =
      window.getSelection();

    if (
      !selection ||
      selection.rangeCount === 0
    ) {
      if (editorRef.current) {
        editorRef.current.appendChild(
          document.createTextNode(
            emoji
          )
        );

        checkEmpty();
      }

      return;
    }

    const range =
      selection.getRangeAt(0);

    const textNode =
      document.createTextNode(
        emoji
      );

    range.deleteContents();
    range.insertNode(
      textNode
    );

    // Position cursor right after emoji.
    const newRange =
      document.createRange();

    newRange.setStartAfter(
      textNode
    );

    newRange.collapse(true);

    selection.removeAllRanges();
    selection.addRange(newRange);

    checkEmpty();
  };

  /**
   * Insert @ from toolbar.
   */
  const insertMentionTrigger = () => {
    editorRef.current?.focus();

    const selection =
      window.getSelection();

    if (
      !selection ||
      selection.rangeCount === 0
    ) {
      return;
    }

    const range =
      selection.getRangeAt(0);

    const textNode =
      document.createTextNode('@');

    range.deleteContents();
    range.insertNode(textNode);

    const newRange =
      document.createRange();

    newRange.setStartAfter(
      textNode
    );

    newRange.collapse(true);

    selection.removeAllRanges();
    selection.addRange(newRange);

    setMentionQuery('');
    setMentionStartIndex(
      Math.max(
        0,
        getTextBeforeCaret().length - 1
      )
    );

    setSelectedMentionIndex(0);

    checkEmpty();
  };

  const handleAttachmentClick = () => {
    if (isUploadingAttachment) {
      return;
    }

    fileInputRef.current?.click();
  };

  const handleFileSelected = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = e.target.files;

    if (
      !files ||
      files.length === 0 ||
      !currentWorkspace
    ) {
      return;
    }

    const file = files[0];

    if (!file) {
      return;
    }

    try {
      setIsUploadingAttachment(true);

      const options: {
        channelId?: string;
        conversationId?: string;
      } = {};

      if (
        activeChat?.type ===
        'channel'
      ) {
        options.channelId =
          activeChat.channel.id;
      } else if (
        activeChat?.type === 'dm'
      ) {
        options.conversationId =
          activeChat.conversation.id;
      }

      const uploaded =
        await api.files.upload(
          currentWorkspace.id,
          file,
          options
        );

      const sizeStr =
        uploaded.fileSize < 1024
          ? `${uploaded.fileSize} B`
          : uploaded.fileSize <
              1024 * 1024
            ? `${(
                uploaded.fileSize /
                1024
              ).toFixed(1)} KB`
            : `${(
                uploaded.fileSize /
                (1024 * 1024)
              ).toFixed(1)} MB`;

      const downloadUrl =
        `/api/v1/workspaces/${currentWorkspace.id}/files/${uploaded.id}/download`;

      editorRef.current?.focus();

      const linkElement =
        document.createElement('a');

      linkElement.href =
        downloadUrl;

      linkElement.target = '_blank';

      linkElement.rel =
        'noopener noreferrer';

      linkElement.className =
        'text-primary font-semibold underline';

      linkElement.textContent =
        `📎 ${uploaded.fileName} (${sizeStr})`;

      const selection =
        window.getSelection();

      if (
        selection &&
        selection.rangeCount > 0
      ) {
        const range =
          selection.getRangeAt(0);

        range.deleteContents();

        range.insertNode(
          linkElement
        );

        const newRange =
          document.createRange();

        newRange.setStartAfter(
          linkElement
        );

        newRange.collapse(true);

        selection.removeAllRanges();

        selection.addRange(
          newRange
        );
      } else if (
        editorRef.current
      ) {
        editorRef.current.appendChild(
          linkElement
        );

        editorRef.current.appendChild(
          document.createTextNode(' ')
        );
      }
    } catch (err: any) {
      console.error(
        'Failed to upload file attachment',
        err
      );

      alert(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to upload attachment'
      );
    } finally {
      setIsUploadingAttachment(false);

      e.target.value = '';

      checkEmpty();
    }
  };

  return (
    <div className="px-4 py-3 border-t border-border bg-card/60 relative select-none shadow-[0_-1px_3px_0_rgba(0,0,0,0.04)]">

      {/* Hidden file input for attachments */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileSelected}
      />

      {/* Mention Autocomplete Dropdown */}
      {mentionQuery !== null && (
        <MentionAutocomplete
          query={mentionQuery}
          members={members}
          onSelect={handleSelectMention}
          onClose={() => {
            setMentionQuery(null);
            setMentionStartIndex(-1);
          }}
          selectedIndex={
            selectedMentionIndex
          }
        />
      )}

      <div className="rounded-xl border border-border bg-background shadow-sm focus-within:ring-1 focus-within:ring-primary/40 focus-within:border-primary/60 transition">

        {/* Formatting Toolbar */}
        <div className="flex items-center gap-0.5 sm:gap-1 px-3 py-1.5 border-b border-border/60 text-muted-foreground flex-wrap">

          {/* Text Size / Headings Dropdown */}
          <div
            className="relative"
            ref={textSizeMenuRef}
          >
            <button
              type="button"
              onClick={() =>
                setShowTextSizeMenu(
                  (prev) => !prev
                )
              }
              title="Text Size (H1, H2, H3)"
              aria-label="Text Size"
              className="p-1.5 rounded-lg hover:bg-muted hover:text-foreground transition flex items-center gap-0.5 text-muted-foreground"
            >
              <Type className="h-4 w-4" />

              <ChevronDown className="h-2.5 w-2.5 opacity-70" />
            </button>

            {showTextSizeMenu && (
              <div className="absolute left-0 bottom-full mb-2 w-48 bg-card border border-border rounded-xl shadow-2xl z-50 py-1 overflow-hidden">

                <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70 border-b border-border/50 bg-muted/30">
                  Text Size
                </div>

                {TEXT_SIZES.map(
                  (size) => (
                    <button
                      key={size.tag}
                      type="button"
                      onClick={() =>
                        applyTextSize(
                          size.tag
                        )
                      }
                      className="w-full text-left px-3 py-2 text-xs text-foreground hover:bg-primary/10 hover:text-primary transition flex items-center justify-between"
                    >
                      <span
                        className={
                          size.sizeClass
                        }
                      >
                        {size.label}
                      </span>
                    </button>
                  )
                )}
              </div>
            )}
          </div>

          {/* Bold */}
          <button
            type="button"
            onClick={() =>
              executeCommand('bold')
            }
            title="Bold"
            aria-label="Bold"
            className="p-1.5 rounded-lg hover:bg-muted hover:text-foreground transition font-bold"
          >
            <Bold className="h-4 w-4" />
          </button>

          {/* Italic */}
          <button
            type="button"
            onClick={() =>
              executeCommand(
                'italic'
              )
            }
            title="Italic"
            aria-label="Italic"
            className="p-1.5 rounded-lg hover:bg-muted hover:text-foreground transition italic"
          >
            <Italic className="h-4 w-4" />
          </button>

          {/* Inline Code */}
          <button
            type="button"
            onClick={
              insertInlineCode
            }
            title="Inline Code"
            aria-label="Inline Code"
            className="p-1.5 rounded-lg hover:bg-muted hover:text-foreground transition"
          >
            <Code className="h-4 w-4" />
          </button>

          {/* Code Block with Language Selector */}
          <div
            className="relative"
            ref={codeMenuRef}
          >
            <div className="flex items-center">

              <button
                type="button"
                onClick={() =>
                  insertCodeBlock('')
                }
                title="Insert Code Block"
                aria-label="Code Block"
                className="p-1.5 rounded-l-lg hover:bg-muted hover:text-foreground transition"
              >
                <SquareCode className="h-4 w-4" />
              </button>

              <button
                type="button"
                onClick={() =>
                  setShowCodeLangMenu(
                    (prev) => !prev
                  )
                }
                title="Select Code Language"
                aria-label="Select Code Language"
                className="p-1 pr-1.5 rounded-r-lg hover:bg-muted hover:text-foreground transition text-muted-foreground"
              >
                <ChevronDown className="h-3 w-3" />
              </button>

            </div>

            {/* Code Language Dropdown */}
            {showCodeLangMenu && (
              <div className="absolute left-0 bottom-full mb-2 w-48 bg-card border border-border rounded-xl shadow-2xl z-50 py-1 max-h-56 overflow-y-auto">

                <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70 border-b border-border/50 bg-muted/30">
                  Select Language
                </div>

                {CODE_LANGUAGES.map(
                  (lang) => (
                    <button
                      key={
                        lang.id ||
                        'plain'
                      }
                      type="button"
                      onClick={() =>
                        insertCodeBlock(
                          lang.id
                        )
                      }
                      className="w-full text-left px-3 py-1.5 text-xs font-mono text-foreground hover:bg-primary/10 hover:text-primary transition flex items-center justify-between"
                    >
                      <span>
                        {lang.label}
                      </span>

                      {lang.id && (
                        <span className="text-[10px] text-muted-foreground opacity-60">
                          .{lang.id}
                        </span>
                      )}
                    </button>
                  )
                )}
              </div>
            )}
          </div>

          {/* Blockquote */}
          <button
            type="button"
            onClick={insertQuote}
            title="Quote"
            aria-label="Quote"
            className="p-1.5 rounded-lg hover:bg-muted hover:text-foreground transition"
          >
            <Quote className="h-4 w-4" />
          </button>

          {/* Mention */}
          <button
            type="button"
            onClick={
              insertMentionTrigger
            }
            title="Mention member (@)"
            aria-label="Mention member"
            className="p-1.5 rounded-lg hover:bg-muted hover:text-foreground transition"
          >
            <AtSign className="h-4 w-4" />
          </button>

          {/* Link */}
          <button
            type="button"
            onClick={insertLink}
            title="Insert Link"
            aria-label="Insert Link"
            className="p-1.5 rounded-lg hover:bg-muted hover:text-foreground transition"
          >
            <LinkIcon className="h-4 w-4" />
          </button>

          {/* Attachment */}
          <button
            type="button"
            onClick={
              handleAttachmentClick
            }
            disabled={
              isUploadingAttachment
            }
            title={
              isUploadingAttachment
                ? 'Uploading file...'
                : 'Attach file'
            }
            aria-label="Attach file"
            className="p-1.5 rounded-lg hover:bg-muted hover:text-foreground transition disabled:opacity-50"
          >
            {isUploadingAttachment ? (
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
            ) : (
              <Paperclip className="h-4 w-4" />
            )}
          </button>

          {/* Translate Draft Button & Language Selector */}
          <div className="relative flex items-center" ref={translateMenuRef}>
            <button
              type="button"
              onClick={() => {
                if (showTranslateBar) {
                  setShowTranslateBar(false);
                  setDraftTranslation(null);
                } else {
                  handleTranslateDraft();
                }
              }}
              title="Translate draft before sending"
              aria-label="Translate draft"
              className={`p-1.5 rounded-l-lg transition flex items-center gap-1 text-xs font-semibold ${
                showTranslateBar || draftTranslation
                  ? 'bg-primary text-primary-foreground shadow-xs'
                  : 'hover:bg-muted hover:text-foreground text-muted-foreground'
              }`}
            >
              {isTranslatingDraft ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Globe className="h-4 w-4" />
              )}
              <span className="text-[11px] uppercase font-mono">{composerTargetLang}</span>
            </button>

            <button
              type="button"
              onClick={() => setShowComposerLangMenu((prev) => !prev)}
              title="Change translation language"
              aria-label="Select target language"
              className={`p-1 pr-1.5 rounded-r-lg transition text-xs ${
                showTranslateBar || draftTranslation
                  ? 'bg-primary text-primary-foreground/90 hover:bg-primary/90'
                  : 'hover:bg-muted hover:text-foreground text-muted-foreground'
              }`}
            >
              <ChevronDown className="h-3 w-3" />
            </button>

            {/* Language Selection Dropdown */}
            {showComposerLangMenu && (
              <div className="absolute left-0 bottom-full mb-2 w-48 bg-popover border border-border rounded-xl shadow-2xl z-50 py-1 max-h-56 overflow-y-auto">
                <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70 border-b border-border/50 bg-muted/30">
                  {t('chat.selectTargetLanguage')}
                </div>
                {TRANSLATION_TARGET_LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => {
                      setComposerTargetLang(lang.code);
                      setShowComposerLangMenu(false);
                      if (!isEmpty) {
                        handleTranslateDraft(lang.code);
                      }
                    }}
                    className={`w-full text-left px-3 py-1.5 text-xs text-foreground hover:bg-primary/10 hover:text-primary transition flex items-center justify-between ${
                      composerTargetLang === lang.code ? 'font-bold text-primary bg-primary/10' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span>{lang.flag}</span>
                      <span>{lang.nativeName}</span>
                    </div>
                    <span className="text-[10px] uppercase font-mono text-muted-foreground opacity-70">
                      {lang.code}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Emoji Picker Popover */}
          <div
            className="relative ml-auto"
            ref={emojiMenuRef}
            style={{
              overflow: 'visible',
            }}
          >
            <button
              type="button"
              onClick={() =>
                setShowEmojiPicker(
                  (prev) => !prev
                )
              }
              title="Insert Emoji"
              aria-label="Insert Emoji"
              className="p-1.5 rounded-lg hover:bg-muted hover:text-foreground transition"
            >
              <Smile className="h-4 w-4" />
            </button>

            {/* Full WhatsApp-style Emoji Picker */}
            {showEmojiPicker && (
              <EmojiPicker
                onSelect={insertEmoji}
                onClose={() =>
                  setShowEmojiPicker(
                    false
                  )
                }
                align="right"
                side="top"
              />
            )}
          </div>
        </div>

        {/* Pre-Send Live Translation Banner */}
        {showTranslateBar && (
          <div className="mx-2.5 my-1.5 p-3 rounded-2xl bg-primary/[0.06] border border-primary/25 shadow-xs text-xs space-y-2 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between gap-2 pb-1 border-b border-primary/20">
              <div className="flex items-center gap-1.5 text-primary font-bold text-xs">
                <Globe className="h-3.5 w-3.5" />
                <span>{t('chat.translationHeader', { lang: getLanguageName(composerTargetLang) })}</span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowTranslateBar(false);
                  setDraftTranslation(null);
                }}
                className="p-1 text-muted-foreground hover:text-foreground rounded-lg transition"
                title="Dismiss"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            {isTranslatingDraft ? (
              <div className="flex items-center gap-2 text-muted-foreground py-1 text-xs">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                <span>{t('chat.translating')}</span>
              </div>
            ) : draftTranslateError ? (
              <div className="flex items-center justify-between text-destructive text-xs">
                <span>{draftTranslateError}</span>
                <button
                  type="button"
                  onClick={() => handleTranslateDraft()}
                  className="font-bold underline text-xs"
                >
                  Retry
                </button>
              </div>
            ) : draftTranslation ? (
              <div className="space-y-2">
                <p className="text-foreground whitespace-pre-wrap font-normal text-xs leading-relaxed bg-card/80 dark:bg-card p-2.5 rounded-xl border border-border">
                  {draftTranslation}
                </p>
                <div className="flex items-center gap-2 justify-end pt-0.5">
                  <button
                    type="button"
                    onClick={handleApplyTranslationToEditor}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl bg-card hover:bg-muted border border-border text-xs font-semibold text-foreground transition shadow-xs"
                    title="Replace input box text with this translation"
                  >
                    <Check className="h-3.5 w-3.5 text-emerald-500" />
                    <span>Apply to Editor</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleTranslateAndSend}
                    disabled={isSending}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-bold transition shadow-xs"
                    title="Send this translated message directly"
                  >
                    {isSending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Send className="h-3.5 w-3.5" />
                    )}
                    <span>Send Translated</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-muted-foreground text-xs italic py-0.5">
                Type your message in the box below and click <strong>🌐 {composerTargetLang.toUpperCase()}</strong> to translate into {getLanguageName(composerTargetLang)}.
              </div>
            )}
          </div>
        )}

        {/* WYSIWYG ContentEditable Input + Send Button */}
        <div className="relative flex items-end gap-2 p-2.5">

          {/* Placeholder Overlay when empty */}
          {isEmpty && (
            <div className="absolute top-2.5 left-3.5 text-sm text-muted-foreground pointer-events-none select-none">
              {placeholder}
            </div>
          )}

          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            onInput={handleEditorInput}
            onKeyDown={handleKeyDown}
            role="textbox"
            aria-multiline="true"
            aria-label={placeholder}
            className="w-full min-h-[24px] max-h-48 overflow-y-auto bg-transparent resize-none text-sm text-foreground focus:outline-none leading-relaxed px-1 select-text"
          />

          <button
            type="button"
            onClick={handleSend}
            disabled={
              isEmpty ||
              isSending
            }
            className="p-2.5 rounded-xl bg-primary text-primary-foreground disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary/90 transition shadow-sm flex-shrink-0"
            title="Send message (Enter)"
            aria-label="Send message"
          >
            <Send className="h-4.5 w-4.5" />
          </button>
        </div>
      </div>
    </div>
  );
};